import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState, SectionCard, SectionToolbar, TableShell, Th } from "../components/ui";
import { useMdcpOrders } from "../hooks";
import { fmtDate } from "../utils";
import type { MdcpOrderListRow } from "../types";

type StatusFilter = "" | "active";
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Aktywne" },
  { value: "", label: "Wszystkie" },
];
const CLOSED_STATUSES = ["completed", "closed", "settled", "cancelled", "rejected"];

function statusColor(status: string): "gray" | "blue" | "green" | "yellow" | "red" {
  if (["completed", "closed", "settled"].includes(status)) return "green";
  if (["cancelled", "rejected"].includes(status)) return "red";
  if (["draft", "pending", "new"].includes(status)) return "gray";
  if (status.includes("accept") || status.includes("review")) return "yellow";
  return "blue";
}

/* Zakładka „MDCP" strony „Zamawianie materiałów" (/power-projects/zamawianie?tab=mdcp):
   zamówienia geodezyjne map do celów projektowych ze WSZYSTKICH projektów ZE w jednej
   tabeli. Samo zamawianie zostaje w panelu MDCP na mapie projektu (punkty foto + obrys
   żyją tam), więc wiersz prowadzi do zamówienia i do projektu. */
export function MdcpOrdersSection() {
  const { data: rows, isLoading } = useMdcpOrders();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  const visible = useMemo(() => {
    const all: MdcpOrderListRow[] = rows ?? [];
    return statusFilter === "active"
      ? all.filter((r) => !CLOSED_STATUSES.includes(r.order_status))
      : all;
  }, [rows, statusFilter]);

  return (
    <div className="space-y-4">
      <SectionToolbar text="Mapa do celów projektowych (MDCP) — zamówienia geodezyjne ze wszystkich projektów ZE. Nowe zamówienie składa się z panelu MDCP na mapie projektu (punkty zdjęciowe + obrys)." />

      <SectionCard
        title="Zamówienia MDCP"
        action={
          <select
            className="input-base !py-0.5 text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        }
      >
        {isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : !visible.length ? (
          <EmptyState>Brak zamówień MDCP — zamawia się je z panelu MDCP na mapie projektu ZE.</EmptyState>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Numer</Th>
                <Th>Projekt ZE</Th>
                <Th>Geodeta</Th>
                <Th>Status</Th>
                <Th>Rodzaj</Th>
                <Th>Punkty</Th>
                <Th>Zakres</Th>
                <Th>Termin</Th>
                <Th>Zamawiający</Th>
                <Th>Utworzono</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-2 py-1.5 font-mono">
                    <Link to={`/orders/${o.order}`} className="hover:underline font-bold">
                      {o.order_number}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">
                    <Link to={`/power-projects/${o.project_id}?tab=map`} className="hover:underline">
                      {o.project_name}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">{o.contractor_name || "—"}</td>
                  <td className="px-2 py-1.5">
                    <Badge color={statusColor(o.order_status)}>{o.order_status_display}</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {o.kind === "primary" ? "główne" : `poprawka${o.is_paid ? "" : " (bezpłatna)"}`}
                  </td>
                  <td className="px-2 py-1.5 font-mono">{o.points_count}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {o.ordered_length_units != null && `${o.ordered_length_units} × 100 mb`}
                    {o.ordered_length_units != null && o.ordered_area_units != null && " · "}
                    {o.ordered_area_units != null && `${o.ordered_area_units} ha`}
                    {o.ordered_length_units == null && o.ordered_area_units == null && "—"}
                  </td>
                  <td className="px-2 py-1.5">{o.deadline ? fmtDate(o.deadline) : "—"}</td>
                  <td className="px-2 py-1.5">{o.created_by_name || "—"}</td>
                  <td className="px-2 py-1.5">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </SectionCard>
    </div>
  );
}
